import BackendApi from '@/services/backend.service'
import { RequestError } from '@/services/errorHandler.service'
import { emptyFlowTemplate } from '@/utils/flowWriteApi'

export type FlowJsonLoadSource = 'remote' | 'local' | 'empty'

export interface FlowJsonLoadResult {
  json: Record<string, unknown>
  source: FlowJsonLoadSource
}

export function parseFlowJson(raw: string | Record<string, unknown> | null | undefined) {
  if (raw == null) {
    return null
  }

  if (typeof raw === 'object') {
    return raw
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export async function fetchFlowJsonById(flowId: number) {
  const response = await BackendApi.get(`/meta/whatsapp/flows/${flowId}`)

  if (!response?.data?.success || response?.data?.data == null) {
    throw new RequestError(response)
  }

  const parsed = parseFlowJson(response.data.data)

  if (!parsed?.screens?.length) {
    throw new RequestError(response)
  }

  return parsed
}

function cloneEmptyFlowTemplate(): Record<string, unknown> {
  return JSON.parse(JSON.stringify(emptyFlowTemplate))
}

function hasValidScreens(json: Record<string, unknown> | null): json is Record<string, unknown> {
  return Boolean(json?.screens && Array.isArray(json.screens) && json.screens.length)
}

export async function loadFlowJsonWithFallback(
  flowId: number,
  localJson?: string | Record<string, unknown> | null
): Promise<FlowJsonLoadResult> {
  try {
    const json = await fetchFlowJsonById(flowId)
    return { json: json as Record<string, unknown>, source: 'remote' }
  } catch {
    const localParsed = parseFlowJson(localJson)

    if (hasValidScreens(localParsed)) {
      return { json: localParsed, source: 'local' }
    }

    return { json: cloneEmptyFlowTemplate(), source: 'empty' }
  }
}
